import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Alert,
  Stack,
  Button,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Divider,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  Person,
  LocationOn,
  Assessment,
  TrendingUp,
  TrendingDown,
  CheckCircle,
  Cancel,
  Pending,
  Schedule,
  Search,
  FilterList,
  Download,
  Refresh,
  Analytics,
  History,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchAppointmentHistory,
  fetchAppointmentAnalytics,
  resetSearchState
} from '../features/appointments/appointmentSearchSlice';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import AppointmentAnalytics from '../components/AppointmentAnalytics';

const AppointmentHistory = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { history, analytics, loading, error: rawError } = useSelector(state => state.appointmentSearch);
  
  // Normalize error to always be a string or null - prevent objects from being rendered
  const error = React.useMemo(() => {
    if (!rawError) return null;
    if (typeof rawError === 'string') return rawError;
    
    // Prefer detail or message fields (most common error formats)
    if (rawError?.detail) {
      const detail = rawError.detail;
      if (typeof detail === 'string') return detail;
      if (typeof detail === 'object') {
        // If detail is an object, try to extract a message from it
        if (detail?.message) return String(detail.message);
        if (detail?.error) return String(detail.error);
        // Otherwise, just return a generic message
        return 'An error occurred';
      }
    }
    
    if (rawError?.message) {
      const message = rawError.message;
      if (typeof message === 'string') return message;
      if (typeof message === 'object') return 'An error occurred';
      return String(message);
    }
    
    // If error is an object but has no detail/message, return generic message
    // Don't stringify the entire object as it might contain nested objects
    return 'Failed to load appointment history';
  }, [rawError]);
  
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateFilter, setDateFilter] = useState('30');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Normalize history results on component mount and when history changes
  const normalizedHistory = React.useMemo(() => {
    if (!history || !history.results) return history;
    
    const normalizeAppointment = (appt) => {
      if (!appt) return appt;
      // Deep clone to avoid mutating original
      const normalized = JSON.parse(JSON.stringify(appt));
      
      // Ensure doctor is always an ID (string or number), never an object
      if (normalized.doctor) {
        if (typeof normalized.doctor === 'object') {
          normalized.doctor = normalized.doctor?.id || normalized.doctor_id || normalized.doctor?.pk || null;
          // If still an object, force to null
          if (typeof normalized.doctor === 'object') {
            console.warn('Doctor is still an object after normalization:', normalized.doctor);
            normalized.doctor = null;
          }
        }
      } else {
        normalized.doctor = normalized.doctor_id || null;
      }
      
      // Ensure patient is always an ID
      if (normalized.patient) {
        if (typeof normalized.patient === 'object') {
          normalized.patient = normalized.patient?.id || normalized.patient_id || normalized.patient?.pk || null;
          if (typeof normalized.patient === 'object') {
            console.warn('Patient is still an object after normalization:', normalized.patient);
            normalized.patient = null;
          }
        }
      } else {
        normalized.patient = normalized.patient_id || null;
      }
      
      // Ensure clinic is always an ID or null
      if (normalized.clinic) {
        if (typeof normalized.clinic === 'object') {
          normalized.clinic = normalized.clinic?.id || normalized.clinic_id || normalized.clinic?.pk || null;
          if (typeof normalized.clinic === 'object') {
            console.warn('Clinic is still an object after normalization:', normalized.clinic);
            normalized.clinic = null;
          }
        }
      } else {
        normalized.clinic = normalized.clinic_id || null;
      }
      
      // Ensure all name fields are strings, not objects
      if (normalized.doctor_name && typeof normalized.doctor_name === 'object') {
        normalized.doctor_name = normalized.doctor_name?.name || normalized.doctor_name?.user?.name || 'Unknown Doctor';
      }
      if (normalized.patient_name && typeof normalized.patient_name === 'object') {
        normalized.patient_name = normalized.patient_name?.name || 'Unknown Patient';
      }
      if (normalized.clinic_name && typeof normalized.clinic_name === 'object') {
        normalized.clinic_name = normalized.clinic_name?.clinic_name || normalized.clinic_name?.name || 'Unknown Clinic';
      }
      
      return normalized;
    };
    
    const normalized = {
      ...history,
      results: Array.isArray(history.results) ? history.results.map(normalizeAppointment) : [],
      appointments: Array.isArray(history.appointments) ? history.appointments.map(normalizeAppointment) : []
    };
    
    // Final check - ensure no objects in results
    normalized.results = normalized.results.map(appt => {
      const check = { ...appt };
      if (check.doctor && typeof check.doctor === 'object') {
        console.error('Found object in doctor field after normalization:', check.doctor);
        check.doctor = null;
      }
      if (check.patient && typeof check.patient === 'object') {
        console.error('Found object in patient field after normalization:', check.patient);
        check.patient = null;
      }
      if (check.clinic && typeof check.clinic === 'object') {
        console.error('Found object in clinic field after normalization:', check.clinic);
        check.clinic = null;
      }
      return check;
    });
    
    return normalized;
  }, [history]);

  // Clear any cached/old data on mount to ensure fresh normalized data
  useEffect(() => {
    // Only reset on initial mount, not on every filter change
    const hasCachedData = history && history.results && history.results.length > 0;
    if (hasCachedData) {
      // Check if cached data has nested objects
      const hasNestedObjects = history.results.some(appt => 
        (appt.doctor && typeof appt.doctor === 'object') ||
        (appt.patient && typeof appt.patient === 'object') ||
        (appt.clinic && typeof appt.clinic === 'object') ||
        (appt.doctor_name && typeof appt.doctor_name === 'object') ||
        (appt.patient_name && typeof appt.patient_name === 'object')
      );
      
      // If nested objects found, clear Redux state and localStorage cache
      if (hasNestedObjects) {
        console.warn('[AppointmentHistory] Detected nested objects in cached data, clearing state...');
        dispatch(resetSearchState());
        // Also clear localStorage cache for appointment search
        try {
          const persistedState = localStorage.getItem('persist:root');
          if (persistedState) {
            const parsed = JSON.parse(persistedState);
            if (parsed.appointmentSearch) {
              const searchState = JSON.parse(parsed.appointmentSearch);
              // Clear history if it has objects
              if (searchState.history?.results?.some(appt => 
                (appt?.doctor && typeof appt.doctor === 'object') ||
                (appt?.patient && typeof appt.patient === 'object')
              )) {
                searchState.history = { results: [], count: 0 };
                parsed.appointmentSearch = JSON.stringify(searchState);
                localStorage.setItem('persist:root', JSON.stringify(parsed));
                console.log('[AppointmentHistory] Cleared corrupted localStorage cache');
              }
            }
          }
        } catch (e) {
          console.error('[AppointmentHistory] Error clearing localStorage:', e);
        }
      }
    }
  }, []); // Only run on mount

  useEffect(() => {
    loadHistory();
  }, [page, searchTerm, statusFilter, dateFilter]);

  useEffect(() => {
    loadAnalytics();
  }, [dateFilter, user?.id, user?.role]);

  const loadHistory = async () => {
    try {
      await dispatch(fetchAppointmentHistory({
        limit,
        offset: (page - 1) * limit,
        search: searchTerm,
        status: statusFilter !== 'All' ? statusFilter : undefined,
        date_from: dateFilter !== 'All' ? getDateFromFilter(dateFilter) : undefined,
      })).unwrap();
    } catch (error) {
      console.error('Failed to load history:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      await dispatch(fetchAppointmentAnalytics({
        period_days: parseInt(dateFilter),
        user_id: user.id,
        user_role: user.role,
      })).unwrap();
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const getDateFromFilter = (filter) => {
    const now = new Date();
    switch (filter) {
      case '7': return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      case '30': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      case '90': return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      default: return undefined;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Completed': 'success',
      'Confirmed': 'info',
      'Pending': 'warning',
      'Cancelled': 'error',
      'No Show': 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Completed': <CheckCircle />,
      'Confirmed': <Schedule />,
      'Pending': <Pending />,
      'Cancelled': <Cancel />,
      'No Show': <Cancel />
    };
    return icons[status] || <Schedule />;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleStatusFilter = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleDateFilter = (event) => {
    setDateFilter(event.target.value);
    setPage(1);
  };

  const handleRefresh = () => {
    loadHistory();
    loadAnalytics();
  };

  const handleExport = () => {
    // Implement export functionality
    console.log('Export history');
  };

  if (loading && !normalizedHistory) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Appointment History & Analytics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View your appointment history and detailed analytics
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab 
            icon={<History />} 
            label="History" 
            iconPosition="start"
          />
          <Tab 
            icon={<Analytics />} 
            label="Analytics" 
            iconPosition="start"
          />
        </Tabs>
      </Card>

      {activeTab === 0 && (
        <Box>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search appointments..."
                    value={searchTerm}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} onChange={handleStatusFilter}>
                      <MenuItem value="All">All Status</MenuItem>
                      <MenuItem value="Completed">Completed</MenuItem>
                      <MenuItem value="Confirmed">Confirmed</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Date Range</InputLabel>
                    <Select value={dateFilter} onChange={handleDateFilter}>
                      <MenuItem value="7">Last 7 days</MenuItem>
                      <MenuItem value="30">Last 30 days</MenuItem>
                      <MenuItem value="90">Last 90 days</MenuItem>
                      <MenuItem value="All">All time</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      startIcon={<Refresh />}
                      onClick={handleRefresh}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={handleExport}
                    >
                      Export
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* History Table */}
          <Card>
            <CardContent>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>Doctor</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && (!normalizedHistory || !normalizedHistory.results || normalizedHistory.results.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : error ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Alert severity="error">
                            {error}
                          </Alert>
                        </TableCell>
                      </TableRow>
                    ) : !normalizedHistory || !normalizedHistory.results || normalizedHistory.results.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <History sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3 }} />
                            <Typography variant="h6" color="text.secondary">
                              No appointment history found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {searchTerm || statusFilter !== 'All'
                                ? 'Try adjusting your search or filters'
                                : 'No appointment history available for the selected period'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      normalizedHistory.results.map((appointment) => {
                        // Final defensive check - ensure absolutely no objects are rendered
                        const safeAppointment = { ...appointment };
                        
                        // Force doctor to be ID or null
                        if (safeAppointment.doctor && typeof safeAppointment.doctor === 'object') {
                          console.error('Doctor is still an object at render time:', safeAppointment.doctor);
                          safeAppointment.doctor = safeAppointment.doctor?.id || safeAppointment.doctor_id || null;
                        }
                        if (typeof safeAppointment.doctor !== 'string' && typeof safeAppointment.doctor !== 'number' && safeAppointment.doctor !== null && safeAppointment.doctor !== undefined) {
                          safeAppointment.doctor = safeAppointment.doctor_id || null;
                        }
                        
                        // Force patient to be ID or null
                        if (safeAppointment.patient && typeof safeAppointment.patient === 'object') {
                          console.error('Patient is still an object at render time:', safeAppointment.patient);
                          safeAppointment.patient = safeAppointment.patient?.id || safeAppointment.patient_id || null;
                        }
                        if (typeof safeAppointment.patient !== 'string' && typeof safeAppointment.patient !== 'number' && safeAppointment.patient !== null && safeAppointment.patient !== undefined) {
                          safeAppointment.patient = safeAppointment.patient_id || null;
                        }
                        
                        // Force clinic to be ID or null
                        if (safeAppointment.clinic && typeof safeAppointment.clinic === 'object') {
                          console.error('Clinic is still an object at render time:', safeAppointment.clinic);
                          safeAppointment.clinic = safeAppointment.clinic?.id || safeAppointment.clinic_id || null;
                        }
                        if (typeof safeAppointment.clinic !== 'string' && typeof safeAppointment.clinic !== 'number' && safeAppointment.clinic !== null && safeAppointment.clinic !== undefined) {
                          safeAppointment.clinic = safeAppointment.clinic_id || null;
                        }
                        
                        // Ensure name fields are strings
                        if (safeAppointment.doctor_name && typeof safeAppointment.doctor_name === 'object') {
                          safeAppointment.doctor_name = safeAppointment.doctor_name?.name || safeAppointment.doctor_name?.user?.name || 'Unknown Doctor';
                        }
                        if (safeAppointment.patient_name && typeof safeAppointment.patient_name === 'object') {
                          safeAppointment.patient_name = safeAppointment.patient_name?.name || 'Unknown Patient';
                        }
                        
                        return (
                      <TableRow key={safeAppointment.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {safeAppointment.scheduled_date ? (
                                typeof safeAppointment.scheduled_date === 'string' ? 
                                  format(new Date(safeAppointment.scheduled_date), 'MMM dd, yyyy') :
                                  format(safeAppointment.scheduled_date, 'MMM dd, yyyy')
                              ) : 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {safeAppointment.scheduled_time || safeAppointment.formatted_time || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Person sx={{ mr: 1, color: 'text.secondary' }} />
                            {safeAppointment.patient_name || 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {/* Absolutely ensure we never render an object */}
                          {(() => {
                            const doctorName = safeAppointment.doctor_name;
                            if (!doctorName) return 'N/A';
                            if (typeof doctorName === 'object') {
                              console.error('doctor_name is an object:', doctorName);
                              return doctorName?.name || doctorName?.user?.name || 'Unknown Doctor';
                            }
                            return String(doctorName);
                          })()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={safeAppointment.type || safeAppointment.consultation_type || 'Consultation'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(safeAppointment.status)}
                            label={safeAppointment.status}
                            color={getStatusColor(safeAppointment.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationOn sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                            {safeAppointment.location || 'Clinic'}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}))}
                  </TableBody>
                </Table>
              </TableContainer>

              {normalizedHistory?.count > limit && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={Math.ceil(normalizedHistory.count / limit)}
                    page={page}
                    onChange={(event, value) => setPage(value)}
                    color="primary"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {activeTab === 1 && (
        <AppointmentAnalytics 
          userRole={user?.role || 'doctor'}
          userId={user?.id}
        />
      )}
    </Container>
  );
};

export default AppointmentHistory;